import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const BlogPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState({
    titulo: '',
    resumen: '',
    contenido: '',
    imagen: '',
    categoria: 'Educación'
  });

  useEffect(() => {
    if (id && id !== 'nuevo') {
      loadPost();
    }
  }, [id]);

  const loadPost = async () => {
    try {
      const response = await api.get(`/blog/posts/my-posts`);
      const foundPost = response.data.find(p => p.id === id);
      if (foundPost) {
        setPost(foundPost);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar el post',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    if (!post.titulo || !post.resumen || !post.contenido) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor completa título, resumen y contenido',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      if (id === 'nuevo') {
        await api.post('/blog/posts', post);
        toast({
          title: '✅ Post creado',
          description: 'Tu post ha sido enviado para revisión del administrador',
        });
      } else {
        await api.put(`/blog/posts/${id}`, post);
        toast({
          title: '✅ Post actualizado',
          description: 'Los cambios han sido guardados',
        });
      }
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo guardar el post',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4">
            <ArrowLeft className="h-5 w-5" />
            Volver al Dashboard
          </Link>
          <h1 className="text-3xl font-bold">
            {id === 'nuevo' ? 'Nuevo Post de Blog' : 'Editar Post'}
          </h1>
          <p className="text-lg opacity-90">
            {id === 'nuevo' 
              ? 'Crea un artículo educativo para compartir con la comunidad' 
              : 'Modifica tu post antes de enviar para revisión'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-[#1e3a8a]">✍️ Editor de Post</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Título del Post *
              </label>
              <Input
                value={post.titulo}
                onChange={(e) => setPost({...post, titulo: e.target.value})}
                placeholder="Ej: Cuidados esenciales para cachorros"
                className="text-lg"
              />
            </div>

            {/* Resumen */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Resumen (breve descripción) *
              </label>
              <Textarea
                value={post.resumen}
                onChange={(e) => setPost({...post, resumen: e.target.value})}
                placeholder="Un resumen corto que aparecerá en la lista de posts..."
                rows={2}
              />
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Categoría
              </label>
              <select
                value={post.categoria}
                onChange={(e) => setPost({...post, categoria: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="Educación">Educación</option>
                <option value="Consejos">Consejos</option>
                <option value="Noticias">Noticias</option>
                <option value="Casos Clínicos">Casos Clínicos</option>
              </select>
            </div>

            {/* Imagen URL */}
            <div>
              <label className="block text-sm font-medium mb-2">
                URL de Imagen (opcional)
              </label>
              <Input
                value={post.imagen || ''}
                onChange={(e) => setPost({...post, imagen: e.target.value})}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              {post.imagen && (
                <img 
                  src={post.imagen} 
                  alt="Preview" 
                  className="mt-2 h-32 object-cover rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            {/* Contenido */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Contenido del Post * 
                <span className="text-xs text-gray-500 ml-2">
                  (Soporta Markdown: # para títulos, ** para negritas, - para listas)
                </span>
              </label>
              <Textarea
                value={post.contenido}
                onChange={(e) => setPost({...post, contenido: e.target.value})}
                placeholder={`# Título Principal

## Subtítulo

Escribe aquí el contenido de tu post...

- Punto 1
- Punto 2

**Texto en negritas**`}
                rows={15}
                className="font-mono text-sm"
              />
            </div>

            {/* Botones */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a]"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Guardando...' : id === 'nuevo' ? 'Enviar para Revisión' : 'Guardar Cambios'}
              </Button>
            </div>

            {id === 'nuevo' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  📝 <strong>Nota:</strong> Tu post será enviado para revisión del administrador antes de publicarse en el sitio.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BlogPostEditor;
